import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, CheckCircle, Clock, MapPin, CreditCard, TrendingUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

export const AgentDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [incoming, setIncoming] = useState<any[]>([]);
  const [branch, setBranch] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetches: Promise<any>[] = [
      apiClient.getDashboardStats(),
      apiClient.listShipments({ status: 'DEPARTED' }, 5, 0),
      apiClient.listShipments({ status: 'IN_TRANSIT' }, 5, 0),
    ];
    if (user?.companyId && user?.branchId) {
      fetches.push(apiClient.getBranch(user.companyId, user.branchId));
    }
    Promise.all(fetches)
      .then(([s, dep, transit, br]) => {
        setStats(s.data.data);
        const depList = dep.data.data?.shipments ?? dep.data.data?.rows ?? [];
        const transitList = transit.data.data?.shipments ?? transit.data.data?.rows ?? [];
        setIncoming([...depList, ...transitList].slice(0, 8));
        if (br) setBranch(br.data.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const shipments = stats?.shipments ?? {};
  const payments = stats?.payments ?? {};
  const pendingRevenue = payments?.pending?.total ?? 0;
  const collectedRevenue = payments?.completed?.total ?? 0;

  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    return d.toLocaleString('default', { month: 'short' });
  });
  const monthlyTrend: any[] = stats?.monthlyTrend ?? [];
  const maxTrend = Math.max(...monthlyTrend.map((m: any) => parseInt(m.count)), 1);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Agent Dashboard</h1>
        <Link to="/agent/shipments/create" className="btn btn-primary"><Package size={16} /> New Shipment</Link>
      </div>

      {/* Branch info banner */}
      {branch ? (
        <div className="card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#1e40af' }}>
            <MapPin size={16} />
            <div>
              <span style={{ fontWeight: 600 }}>{branch.name}</span>
              {branch.address && <span style={{ marginLeft: 8, fontSize: 13 }}>{branch.address}</span>}
              {branch.phone && <span style={{ marginLeft: 8, fontSize: 13 }}>· {branch.phone}</span>}
              <span style={{ marginLeft: 8, fontSize: 12, color: '#3b82f6' }}>You can only receive shipments at this branch.</span>
            </div>
          </div>
        </div>
      ) : user?.branchId && (
        <div className="card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#1e40af' }}>
            <MapPin size={16} />
            <span>Assigned branch: <strong>{user.branchId}</strong>. You can only receive shipments at this branch.</span>
          </div>
        </div>
      )}

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <>
          {/* Stats grid */}
          <div className="stats-grid">
            {[
              { label: 'Incoming Shipments', value: incoming.length, color: '#f59e0b', icon: <Clock size={20} />, link: '/agent/shipments?status=IN_TRANSIT' },
              { label: 'Delivered', value: shipments.delivered ?? 0, color: '#22c55e', icon: <CheckCircle size={20} />, link: '/agent/shipments?status=DELIVERED' },
              { label: 'Delivery Rate', value: `${shipments.deliveryRate ?? 0}%`, color: '#6366f1', icon: <TrendingUp size={20} />, link: '/agent/shipments' },
              { label: 'Total Shipments', value: shipments.total ?? 0, color: '#3b82f6', icon: <Package size={20} />, link: '/agent/shipments' },
              { label: 'Pending Revenue', value: `${Number(pendingRevenue).toLocaleString()} RWF`, color: '#f59e0b', icon: <CreditCard size={20} />, link: '/agent/payments' },
              { label: 'Collected Revenue', value: `${Number(collectedRevenue).toLocaleString()} RWF`, color: '#22c55e', icon: <CreditCard size={20} />, link: '/agent/payments' },
            ].map(s => (
              <Link to={s.link} key={s.label} style={{ textDecoration: 'none' }}>
                <div className="stat-card" style={{ borderLeft: `4px solid ${s.color}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value" style={{ color: s.color, fontSize: typeof s.value === 'string' && s.value.length > 8 ? 16 : 28 }}>{s.value}</div>
                    </div>
                    <div style={{ color: s.color, opacity: 0.6 }}>{s.icon}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Monthly trend */}
          {monthlyTrend.length > 0 && (
            <div className="card" style={{ marginTop: 20 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Monthly Shipments (Last 6 Months)</h2>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 100 }}>
                {monthlyTrend.map((m: any) => (
                  <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{m.count}</div>
                    <div style={{ width: '100%', height: `${(parseInt(m.count) / maxTrend) * 80}px`, minHeight: parseInt(m.count) > 0 ? 4 : 0, background: 'linear-gradient(180deg, #3b82f6, #6366f1)', borderRadius: '4px 4px 0 0', transition: 'height 0.6s ease' }} />
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.month?.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Incoming shipments table */}
          {incoming.length > 0 && (
            <div className="card" style={{ marginTop: 20 }}>
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>Incoming Shipments ({incoming.length})</h2>
                <Link to="/agent/shipments?status=IN_TRANSIT" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Tracking #</th><th>Sender</th><th>Receiver</th><th>Status</th><th>Amount</th><th></th></tr></thead>
                  <tbody>
                    {incoming.map((s: any) => (
                      <tr key={s.id}>
                        <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                        <td>{s.senderName}</td>
                        <td>{s.receiverName}</td>
                        <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                        <td>{Number(s.totalAmount).toLocaleString()} RWF</td>
                        <td>
                          <Link to={`/agent/shipments/${s.id}/deliver`} className="btn btn-success" style={{ padding: '4px 10px', fontSize: 12 }}>
                            <CheckCircle size={12} /> Receive
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};
